/**
 * App themes: page background + Ionic semantic colours tuned for contrast on that canvas.
 * Colour bases follow Ionic's generator; applied as full `--ion-color-*` sets at runtime.
 * @see https://ionicframework.com/docs/theming/colors
 */

import { ROBLOX_PAGE_BACKGROUND_PALETTE } from './page-background-palette';

export interface AppThemeColors {
  readonly primary: string;
  readonly secondary: string;
  readonly tertiary: string;
  readonly success: string;
  readonly warning: string;
  readonly danger: string;
  readonly light: string;
  readonly medium: string;
  readonly dark: string;
}

export interface AppTheme {
  readonly id: string;
  readonly name: string;
  readonly brickNumber: number;
  readonly rgb: readonly [number, number, number];
  readonly pageBackground: string;
  readonly colors: AppThemeColors;
}

/** Curated Ionic palettes per page tint (readable buttons on that background). */
const THEME_COLOR_OVERRIDES: Record<string, AppThemeColors> = {
  white: {
    primary: '#0d3d6e',
    secondary: '#1a5c4a',
    tertiary: '#452070',
    success: '#157a2e',
    warning: '#946800',
    danger: '#b91c1c',
    light: '#f4f5f8',
    medium: '#5f6368',
    dark: '#121212',
  },
  paper: {
    primary: '#1e5631',
    secondary: '#0d3d6e',
    tertiary: '#5c3d8a',
    success: '#157a2e',
    warning: '#946800',
    danger: '#b91c1c',
    light: '#fafaf9',
    medium: '#57534e',
    dark: '#1c1917',
  },
  'cool-gray': {
    primary: '#0d47a1',
    secondary: '#1b5e20',
    tertiary: '#4a148c',
    success: '#2e7d32',
    warning: '#9a6700',
    danger: '#c62828',
    light: '#f4f6f8',
    medium: '#546e7a',
    dark: '#121212',
  },
  charcoal: {
    primary: '#7eb0e3',
    secondary: '#72b89a',
    tertiary: '#a89fd0',
    success: '#7bc992',
    warning: '#d9b45c',
    danger: '#e07878',
    light: '#2e2e2e',
    medium: '#a3a3a3',
    dark: '#ececec',
  },
  slate: {
    primary: '#8ab8e8',
    secondary: '#6ec4a8',
    tertiary: '#b0a6d8',
    success: '#76c896',
    warning: '#e0c060',
    danger: '#e08080',
    light: '#353b46',
    medium: '#9aa4b2',
    dark: '#e8eaee',
  },
  midnight: {
    primary: '#93c5fd',
    secondary: '#5eead4',
    tertiary: '#a5b4fc',
    success: '#6ee7b0',
    warning: '#fde047',
    danger: '#fca5a5',
    light: '#1e2836',
    medium: '#8899aa',
    dark: '#eef2f7',
  },
  'deep-forest': {
    primary: '#86efac',
    secondary: '#7dd3fc',
    tertiary: '#c4b5fd',
    success: '#4ade80',
    warning: '#fbbf24',
    danger: '#f87171',
    light: '#1e3328',
    medium: '#7a9488',
    dark: '#ecfdf5',
  },
  'tr-green': {
    primary: '#1e5631',
    secondary: '#1a4a6e',
    tertiary: '#4a3d78',
    success: '#157a2e',
    warning: '#946800',
    danger: '#b91c1c',
    light: '#eef5ef',
    medium: '#4a6350',
    dark: '#142318',
  },
  'tr-blue': {
    primary: '#0d3d6e',
    secondary: '#1a5c4a',
    tertiary: '#3d2878',
    success: '#0f6b32',
    warning: '#8a6400',
    danger: '#b91c1c',
    light: '#eef4fa',
    medium: '#3d5870',
    dark: '#0a2238',
  },
  'tr-red': {
    primary: '#5a1210',
    secondary: '#1a3d5c',
    tertiary: '#4a2878',
    success: '#157a2e',
    warning: '#946800',
    danger: '#7f1d1d',
    light: '#faf0ef',
    medium: '#6b4543',
    dark: '#2a0a09',
  },
  'neon-orange': {
    primary: '#6b3410',
    secondary: '#1a4a6e',
    tertiary: '#5c3d8a',
    success: '#157a2e',
    warning: '#946800',
    danger: '#b91c1c',
    light: '#faf3ed',
    medium: '#6b5344',
    dark: '#2a1808',
  },
  'faded-green': {
    primary: '#1a3d24',
    secondary: '#1a4a6e',
    tertiary: '#4a3d78',
    success: '#157a2e',
    warning: '#946800',
    danger: '#b91c1c',
    light: '#eef3ef',
    medium: '#4a5c50',
    dark: '#121f16',
  },
  'bright-violet': {
    primary: '#452070',
    secondary: '#1a5c4a',
    tertiary: '#0d3d6e',
    success: '#157a2e',
    warning: '#946800',
    danger: '#b91c1c',
    light: '#f3ecfa',
    medium: '#705890',
    dark: '#180828',
  },
  'lilac': {
    primary: '#4a2878',
    secondary: '#1a5c4a',
    tertiary: '#0d3d6e',
    success: '#157a2e',
    warning: '#946800',
    danger: '#b91c1c',
    light: '#f5eef8',
    medium: '#6b5478',
    dark: '#1e1228',
  },
  'royal-purple': {
    primary: '#e8dcf5',
    secondary: '#b8dcc8',
    tertiary: '#b8cce8',
    success: '#6ee7a0',
    warning: '#fcd34d',
    danger: '#fca5a5',
    light: '#f0eaf5',
    medium: '#9878a8',
    dark: '#180818',
  },
  'storm-blue': {
    primary: '#dceaf8',
    secondary: '#b8dcc8',
    tertiary: '#d4c8f0',
    success: '#6ee7a0',
    warning: '#fcd34d',
    danger: '#fca5a5',
    light: '#eef4fa',
    medium: '#8aa0b8',
    dark: '#121820',
  },
};

const DEFAULT_COLORS: AppThemeColors = {
  primary: '#0d3d6e',
  secondary: '#1a5c4a',
  tertiary: '#3d2878',
  success: '#157a2e',
  warning: '#946800',
  danger: '#b91c1c',
  light: '#f4f5f8',
  medium: '#5f5f5f',
  dark: '#121212',
};

export const APP_THEMES: readonly AppTheme[] =
  ROBLOX_PAGE_BACKGROUND_PALETTE.map((row) => ({
    id: row.id,
    name: row.name,
    brickNumber: row.brickNumber,
    rgb: row.rgb,
    pageBackground: row.cssHex,
    colors: THEME_COLOR_OVERRIDES[row.id] ?? DEFAULT_COLORS,
  }));

/** Default for new installs and when no valid preference is stored. */
export const DEFAULT_APP_THEME_ID = APP_THEMES[1]?.id ?? 'paper';

export function appThemeById(id: string): AppTheme {
  return (
    APP_THEMES.find((t) => t.id === id) ??
    APP_THEMES.find((t) => t.id === DEFAULT_APP_THEME_ID) ??
    APP_THEMES[0]
  );
}
